import React, { useEffect, useRef, useState } from 'react';
import {
  InvoiceContainer,
  Header,
  CompanyInfo,
  InvoiceDetails,
  Table,
  TableRow,
  TableHeader,
  TableData,
  PaymentInfo,
  Divider,
  PaymentAdvice,
  LogoImg,
  HeaderWrapper,
  TextWrapper,
} from './invoice.style';

import Logo from '../../../assets/PNG/transparentLogo.png';
import { LoaderWrapper } from '../../../services/settings/appStyle/global.style';
import { Label } from '../../../Components/uielements';
import Spinner from '../../../Components/uielements/loader/spinner';
import { useParams } from 'react-router-dom';
import { getInvoiceData } from '../../../services/bioplus-services/invoice';
import { ErrorSvg } from '../../../Components/icons';
import moment from 'moment';

function generateInvoiceNumber() {
  const randomNumber = Math.floor(1000 + Math.random() * 9000); // Generate a random 4-digit number
  return `INV-${randomNumber}`;
}

const Invoice = () => {
  const { companyId, userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const invoiceNumber = generateInvoiceNumber();

  const componentRef = useRef(null);
  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        setLoading(true);
        let data = await getInvoiceData(companyId, userId);
        setInvoiceData(data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [companyId, userId]);

  if (loading) {
    return (
      <>
        <LoaderWrapper>
          <Spinner size={'extraMedium'} />
          <br />
          <Label size="header3">Sit back and relax, We are generating your Invoice.</Label>
        </LoaderWrapper>
      </>
    );
  }

  if (error) {
    return (
      <LoaderWrapper>
        <div style={{ display: 'grid', gap: '20px' }}>
          <ErrorSvg style={{ width: '100px', height: '100px', justifySelf: 'center' }} />
          <Label size="header3">Error loading Invoice data. Please try again later.</Label>;
        </div>
      </LoaderWrapper>
    );
  }

  return (
    <>
      {invoiceData && invoiceData !== undefined ? (
        <InvoiceContainer ref={componentRef} id="printable">
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={handlePrint}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007BFF',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                position: 'absolute',
                right: '-1px',
                top: '-1px',
              }}
            >
              <span style={{ marginRight: '8px' }}>📄</span>
              Download PDF
            </button>
          </div>
          <HeaderWrapper>
            <Header>
              <h1>DRAFT INVOICE</h1>
              <p>Stewart McConachy</p>
            </Header>

            <CompanyInfo>
              <LogoImg src={Logo} alt="BioPlus Logo" />
              <p>
                <strong>Invoice Date:</strong>{' '}
                {moment(invoiceData?.subscriptionData?.createdAt).format('DD MMM YYYY')}
              </p>
              <p>
                <strong>Invoice Number:</strong> {invoiceNumber}
              </p>
              <p>
                <strong>ABN:</strong> 85 609 447 841
              </p>
              <p>iTRAKASSETS Trading as AGManager™</p>
              <p>
                <strong>Attention:</strong> Stewart McConachy
              </p>
              <p>PO Box 2128, East Grovedale VIC 3216, Australia</p>
            </CompanyInfo>
          </HeaderWrapper>

          <Table>
            <thead>
              <TableRow>
                <TableHeader>Description</TableHeader>
                <TableHeader>Quantity</TableHeader>
                <TableHeader>Unit Price</TableHeader>
                {/* <TableHeader>GST</TableHeader>  */}
                <TableHeader>Amount AUD</TableHeader>
              </TableRow>
            </thead>
            <tbody>
              <TableRow>
                <>
                  {/* BIOPLUS® Standard Annually - One Property ID, 100 X Geofences, 10 X Members, 10 X
                  Timesheets, 10 X eCVD (Per Month), Basic Management & Basic Reports */}
                  {(() => {
                    const features = invoiceData?.subscriptionData?.subscriptionId?.features ?? [];
                    const quantity = invoiceData?.subscriptionData?.quantity ?? 1;

                    const getLimit = (label) => features.find((f) => f?.label === label)?.limit;

                    const formatFeature = (label, displayName) => {
                      const limit = getLimit(label);
                      if (limit === null || limit === undefined) {
                        return displayName;
                      }
                      return `${limit * quantity} ${displayName}`;
                    };

                    return (
                      <TableData>
                        {`${invoiceData?.subscriptionData?.labelName} - ${formatFeature(
                          'premise',
                          'Properties'
                        )}, ${formatFeature('geofence', 'Geofences')}, ${formatFeature(
                          'user',
                          'Members'
                        )}, ${formatFeature('timesheet', 'Timesheets')}, ${formatFeature(
                          'edec',
                          'eCVD'
                        )}, Basic Management & Basic Reports`}
                      </TableData>
                    );
                  })()}
                </>
                <TableData>{invoiceData?.subscriptionData?.quantity}</TableData>
                <TableData>{Number(invoiceData?.subscriptionData?.baseCost).toFixed(2)}</TableData>
                {/* <TableData>10%</TableData> */}
                <TableData>
                  {Number(
                    invoiceData?.subscriptionData?.quantity *
                      invoiceData?.subscriptionData?.baseCost
                  ).toFixed(2)}
                </TableData>
              </TableRow>
            </tbody>
          </Table>

          <InvoiceDetails>
            <p>
              <strong>Subtotal:</strong>{' '}
              {Number(
                invoiceData?.subscriptionData?.quantity * invoiceData?.subscriptionData?.baseCost
              ).toFixed(2)}
            </p>
            {/* <p>
              <strong>TOTAL GST 10%:</strong> 132.00
            </p> */}
            <p>
              <strong>TOTAL AUD:</strong>{' '}
              {Number(
                invoiceData?.subscriptionData?.quantity * invoiceData?.subscriptionData?.baseCost
              ).toFixed(2)}
            </p>
          </InvoiceDetails>

          <PaymentInfo>
            <p>
              <strong>Due Date: {moment(invoiceData?.dueDate).format('DD MMM YYYY')}</strong>
            </p>
            <p>
              Monthly payment can be made via our payment gateway - STRIPE. <br />
              <strong>OR</strong> Make payable to:
            </p>
            <p>
              iTRAKAssets Pty Ltd trading as AGManager™ <br />
              Account: iTRAKassets Pty Ltd CBA <br />
              BSB: 063-504 Account No: 10 585 283
            </p>
          </PaymentInfo>

          <Divider />

          <PaymentAdvice>
            <div>
              <h1>PAYMENT ADVICE</h1>
              <TextWrapper>
                <p style={{ justifySelf: 'flex-start' }}>To:</p>
                <p>
                  iTRAKASSETS Trading as AGManager™ <br />
                  Attention: Stewart McConachy <br />
                  PO Box 2128, East Grovedale VIC 3216, Australia
                </p>
              </TextWrapper>
            </div>
            <div>
              <div>
                <p style={{ textTransform: 'capitalize' }}>
                  <strong>Customer:</strong>{' '}
                  {`${invoiceData?.subscriptionData?.userId?.firstName} ${invoiceData?.subscriptionData?.userId?.lastName}`}
                </p>
                <p>
                  <strong>Invoice Number:</strong> {invoiceNumber}
                </p>
                <p>
                  <strong>
                    Amount Due:{' '}
                    {Number(
                      invoiceData?.subscriptionData?.quantity *
                        invoiceData?.subscriptionData?.baseCost
                    ).toFixed(2)}{' '}
                    AUD
                  </strong>
                </p>
                <p>
                  <strong>Due Date: {moment(invoiceData?.dueDate).format('DD MMM YYYY')}</strong>
                </p>
              </div>
              <p>
                <strong>Amount Enclosed:</strong> ...................................
                <p>Enter the amount you are paying above.</p>
              </p>
            </div>
          </PaymentAdvice>
        </InvoiceContainer>
      ) : (
        <></>
      )}
    </>
  );
};

export default Invoice;
